import { FC, Fragment, useEffect, useState } from 'react';
import { Button, Form, Modal, Row, Space, Switch, Table, Tooltip } from 'antd';
import {
	InfoCircleOutlined,
	PlusOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/App';
import CardPanel from '@/components/CardPanel';
import { FieldType, renderField } from '@/components/FormBuilder';
import formRule from '@/libs/antd/rules';
import {
	GroupMenu,
	GroupMenuPayload,
	createGroupMenu,
	getGroupMenus,
} from '@/libs/fleet-api/services/group-menus';
import useQuerySelect from '@/hooks/useQuerySelect';
import { getMasterGroups } from '@/libs/fleet-api/services/master-group';
import { getMasterMenus } from '@/libs/fleet-api/services/master-menus';

const GroupMenuPage: FC = () => {
	const { notification } = useApp();
	const { data, isLoading, isFetching, refetch } = useQuery({
		queryKey: ['getGroupMenus'],
		queryFn: () => getGroupMenus(),
	});

	const selectMasterGroups = useQuerySelect({
		queryKey: ['getMasterGroups'],
		queryFn: async () => {
			const { data } = await getMasterGroups();
			return data.map((group) => {
				return {
					value: group.id,
					label: group.name,
				};
			});
		},
	});

	const selectMasterMenus = useQuerySelect({
		queryKey: [],
		queryFn: async () => {
			const { data } = await getMasterMenus();
			return data.map((menu) => {
				return {
					value: menu.id,
					label: menu.title,
				};
			});
		},
	});

	const dataSource =
		data?.data.map((value, key) => {
			return {
				...value,
				key,
			};
		}) ?? [];

	const [modalDetailForm] = Form.useForm<GroupMenuPayload>();
	const [modalDetail, setModalDetail] = useState<
		| {
				type: 'create';
		  }
		| {
				type: 'update';
				data: GroupMenu;
		  }
	>();

	useEffect(() => {
		if (modalDetail?.type === 'update') {
			const { isActive, masterGroupId, masterMenuId } = modalDetail.data;
			modalDetailForm.setFieldsValue({
				isActive,
				masterGroupId,
				masterMenuIds: [masterMenuId],
			});
		}
	}, [modalDetail, modalDetailForm]);

	return (
		<Fragment>
			<CardPanel
				title="Group Menu"
				extra={
					<Space size="small">
						<Button
							icon={<ReloadOutlined />}
							loading={isFetching}
							onClick={() => refetch()}
						>
							Refresh
						</Button>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setModalDetail({ type: 'create' });
							}}
						>
							New Entry
						</Button>
					</Space>
				}
				styles={{
					header: {
						paddingRight: 12,
					},
				}}
			>
				<Table
					dataSource={dataSource}
					loading={isLoading || isFetching}
					columns={[
						{
							title: '#',
							align: 'center',
							width: 60,
							render: (_, __, index) => {
								return index + 1;
							},
						},
						{
							title: 'Group ID',
							ellipsis: true,
							render: (_, record) => (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										setModalDetail({
											type: 'update',
											data: record,
										});
									}}
								>
									{record.masterGroupId}
								</a>
							),
						},
						Table.EXPAND_COLUMN,
						{
							title: 'Menu ID',
							ellipsis: true,
							render: (_, record) => record.masterMenuId,
						},
						{
							title: 'Status',
							align: 'center',
							dataIndex: 'isActive',
							render: (isActive) => (
								<Switch
									value={isActive}
									checkedChildren="Active"
									unCheckedChildren="Inactive"
									style={{ cursor: 'default' }}
								/>
							),
						},
						{
							title: 'Created',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
						{
							title: 'Updated',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
					]}
					expandable={{
						expandedRowRender(record) {
							return (
								<div style={{ padding: 16 }}>
									<pre style={{ margin: 0 }}>
										{JSON.stringify(record, null, 2)}
									</pre>
								</div>
							);
						},
					}}
				/>
			</CardPanel>

			<Modal
				open={Boolean(modalDetail)}
				width={500}
				title={modalDetail?.type === 'update' ? 'Edit' : 'New'}
				afterClose={() => modalDetailForm.resetFields()}
				onCancel={() => setModalDetail(undefined)}
				onOk={() => modalDetailForm.submit()}
			>
				<Form
					form={modalDetailForm}
					layout="vertical"
					onFinish={async (values) => {
						try {
							if (modalDetail?.type === 'update') {
								// await updateMasterGroup(modalDetail.data.id, values);
								// notification.success({
								// 	message: 'Updated',
								// 	description: 'data was successfully updated',
								// });
							} else {
								await createGroupMenu(values);
								notification.success({
									message: 'Created',
									description: 'data was successfully created',
								});
							}
							setModalDetail(undefined);
							refetch();
						} catch (error) {
							console.log(error);
						}
					}}
				>
					<Row gutter={[16, 16]}>
						{(
							[
								{
									name: 'masterGroupId',
									type: 'select',
									label: 'Master Group',
									config: {
										placeholder: 'Select Master Group',
										...selectMasterGroups,
									},
									rules: [formRule.required()],
								},
								{
									name: 'masterMenuIds',
									type: 'select',
									label: 'Master Menu',
									config: {
										mode:
											modalDetail?.type === 'create' ? 'multiple' : undefined,
										placeholder: 'Select Master Menu',
										...selectMasterMenus,
									},
									rules: [formRule.required()],
								},
								{
									name: 'isActive',
									type: 'switch',
									label: 'Status',
									width: '1/2',
									config: {
										checkedChildren: 'Active',
										unCheckedChildren: 'Inactive',
									},
								},
							] as FieldType[]
						).map((field, key) => renderField({ ...field, key }))}
					</Row>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default GroupMenuPage;
